import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';

/**
 * BalancePlanChip — glass-пилл кредиты + план в верхней зоне Home.
 * Figma: 318:3052 (`balance-plan`).
 *
 * Geometry (from strict-audit §2 — закрывает 7 deviations):
 *  - Root horizontal auto-layout, padding 4/4/3/3, gap 6, cornerRadius 16
 *  - Root fill = GRADIENT_LINEAR handle (1,0.5)→(0,0.5) — horizontal
 *    right→left, stops glass-24-start (α 0.24) → glass-24-end (α 0.08)
 *  - backdropBlur:12 (glass-strong)
 *  - NO stroke/border (Figma strokes:[]) — any `border-white/10` is invented
 *  - Balance frame: itemSpacing 2, NO padding (code `pl-1` was invented)
 *  - Plan sub-chip: padding 6/6/2/2, gap 2, cornerRadius 16,
 *    fill = GRADIENT_LINEAR same direction, stops 0.24 → 0.04 (NOT 0.08) →
 *    uses new glass-plan-end token.
 *
 * Icons come from primitives/Icon (real Figma SVGs in /public/icons/):
 *  - coin         — Figma 123:3397 (circle minus 4-pt sparkle cutout,
 *                    fill icon/accent #FFAC0B via text-coin-yellow)
 *  - chevron-right— Figma 368:3629 (bold solid-fill chevron)
 *
 * Весь блок кликабельный (handoff §3, §13) — tap → navigate('/profile').
 */
export default function BalancePlanChip({
  credits = 0,
  planLabel = 'Free',
  onClick,
  loading = false,
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={loading
        ? t('common.loading')
        : t('profile.balanceAria', {
            credits,
            plan: planLabel,
          })}
      disabled={loading}
      className={[
        'inline-flex items-center gap-[7.5px]',
        'px-[5px] py-[3.75px]',
        'rounded-home-20',
        // Figma gradient direction: handle (1,0.5)→(0,0.5) = right→left =
        // Tailwind `bg-gradient-to-l`. Stops 24% → 8% via glass-24 tokens.
        'bg-gradient-to-l from-glass-24-start to-glass-24-end',
        'backdrop-blur-glass-strong',
        'transition-transform active:scale-[0.97]',
      ].join(' ')}
    >
      {/* balance (coin + number) — Figma balance frame: gap 2, no padding */}
      <span className="flex items-center gap-[2.5px]">
        <Icon name="coin" size={22} className="text-coin-yellow" />
        {loading ? (
          <span
            aria-hidden="true"
            className="block h-[18px] w-[34px] rounded-full skeleton-shimmer"
            style={{ '--skeleton-base': 'rgba(255,255,255,0.20)' }}
          />
        ) : (
          <span
            className="font-sans font-bold text-[18px] leading-[21.132px] text-white"
          >
            {credits}
          </span>
        )}
      </span>

      {/* plan sub-chip — Figma plan frame: padding 6/6/2/2, gap 2, radius 16,
          gradient stops 0.24 → 0.04 (end-alpha HALF of root chip). */}
      <span
        className={[
          'flex items-center gap-[2.5px]',
          'px-[7.5px] py-[2.5px]',
          'rounded-home-20',
          // Figma sub-chip gradient: same direction, 0.24 → 0.04
          'bg-gradient-to-l from-glass-24-start to-glass-plan-end',
          'backdrop-blur-glass-strong',
        ].join(' ')}
      >
        {loading ? (
          <span
            aria-hidden="true"
            className="block h-[15px] w-[44px] rounded-full skeleton-shimmer"
            style={{ '--skeleton-base': 'rgba(255,255,255,0.20)' }}
          />
        ) : (
          <span className="font-sans font-medium text-[15px] leading-[17.61px] text-white">{planLabel}</span>
        )}
        <Icon name="chevron-right" size={15} className="text-white" />
      </span>
    </button>
  );
}
